import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useCart } from "@/store/useCart";
import { formatPrice } from "@/lib/utils";

interface Product {
    id: string;
    name: string;
    slug: string;
    price: number;
    salePrice?: number;
    images: string[];
    description?: string;
}

interface ProductCardProps {
    product: Product;
}

export function ProductCard({ product }: ProductCardProps) {
    const { addItem } = useCart();
    const [quickViewOpen, setQuickViewOpen] = useState(false);

    const handleAddToCart = () => {
        addItem({
            id: product.id,
            name: product.name,
            price: product.salePrice || product.price,
            image: product.images[0],
            quantity: 1,
        });
        // Optional: Open cart drawer or show toast
    };

    return (
        <div className="group relative overflow-hidden rounded-lg border bg-background transition-all hover:shadow-lg">
            {/* Image */}
            <div className="aspect-square overflow-hidden bg-muted relative">
                {product.images?.[0] ? (
                    <img
                        src={product.images[0]}
                        alt={product.name}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground bg-gray-100">
                        No Image
                    </div>
                )}

                {/* Overlay Buttons */}
                <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                    <Sheet open={quickViewOpen} onOpenChange={setQuickViewOpen}>
                        <SheetTrigger asChild>
                            <Button variant="secondary" size="icon" className="h-10 w-10 text-primary rounded-full hover:scale-110 transition-transform" title="Quick View">
                                <Eye className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="bottom" className="h-[80vh] sm:h-auto sm:max-w-3xl sm:side-center rounded-t-xl sm:rounded-xl">
                            <div className="grid gap-8 p-6 sm:grid-cols-2">
                                <div className="aspect-square bg-muted rounded-lg overflow-hidden">
                                    {product.images?.[0] && <img src={product.images[0]} className="w-full h-full object-cover" />}
                                </div>
                                <div className="space-y-4">
                                    <SheetHeader className="text-left px-0">
                                        <SheetTitle className="text-2xl">{product.name}</SheetTitle>
                                    </SheetHeader>
                                    <div className="text-xl font-bold text-primary">
                                        {product.salePrice ? (
                                            <div className="flex gap-2 items-end">
                                                <span>{formatPrice(product.salePrice)}</span>
                                                <span className="text-sm text-muted-foreground line-through decoration-destructive">{formatPrice(product.price)}</span>
                                            </div>
                                        ) : formatPrice(product.price)}
                                    </div>
                                    <div className="text-muted-foreground">
                                        {product.description || "No description available."}
                                    </div>
                                    <div className="flex gap-4 pt-4">
                                        <Button size="lg" className="w-full" onClick={() => {
                                            handleAddToCart();
                                            setQuickViewOpen(false);
                                        }}>
                                            Add to Cart
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>

            {/* Content */}
            <div className="p-4">
                <Link to={`/products/${product.slug}`} className="block">
                    <h3 className="line-clamp-1 text-lg font-medium hover:underline">{product.name}</h3>
                </Link>
                <div className="mt-2 flex items-center justify-between">
                    <div className="font-semibold text-lg">
                        {product.salePrice ? (
                            <div className="flex flex-col leading-none">
                                <span className="text-destructive font-bold">{formatPrice(product.salePrice)}</span>
                                <span className="text-xs text-muted-foreground line-through">{formatPrice(product.price)}</span>
                            </div>
                        ) : formatPrice(product.price)}
                    </div>
                    <Button size="sm" onClick={handleAddToCart}>
                        <ShoppingCart className="mr-2 h-4 w-4" /> Add
                    </Button>
                </div>
            </div>
        </div>
    );
}
